import {useIsAgency} from "@/hooks/useUserHooks";
import {selectedCompanyIdAtom} from "@/store/jotaiAtoms";
import useUserStore from "@/store/user";
import {getUserPermission} from "@/utils/permissionUtils";
import {AbilityBuilder, CreateAbility, createMongoAbility, ForcedSubject, MongoAbility} from "@casl/ability";
import {createContextualCan} from "@casl/react";
import {useAtomValue} from "jotai";
import {first} from "lodash";
import {createContext, FC, PropsWithChildren} from "react";

const actions = ["create", "update", "read", "delete", "download", "list"] as const;
const subjects = [
  "ApplicantCardDetails",
  "ApplicantAdminOperation",
  "AdminMenu",
  "AgencyDetails",
  "AmbassadorApplication",
  "ApplicantOperation",
  "BlockedInfluencer",
  "BrandPagesMenu",
  "BuyoutPurchase",
  "Campaign",
  "CampaignAddresses",
  "CampaignAssetUpload",
  "CampaignAdminOnlyOptions",
  "CampaignDrafts",
  "CampaignInvite",
  "CampaignPerformance",
  "CampaignReport",
  "Campaigns",
  "CampaignStart",
  "CampaignVouchers",
  "ChatMessage",
  "ChatMessages",
  "ChatOperation",
  "CommercialPhaseDeadline",
  "CompanyBillingAddress",
  "CompanyContentBuyouts",
  "CompanyCredits",
  "CompanyDetails",
  "CompanyInvite",
  "CompanyMember",
  "CompanyMemberInvitation",
  "CompanyMembers",
  "CompanyOwner",
  "CompanyPassword",
  "CompanyPaymentMethod",
  "CompanyPlan",
  "CompanyReferral",
  "Content",
  "ContentDetails",
  "ContentPurchase",
  "ContentReview",
  "DisciplinaryOperation",
  "FavoriteContentLike",
  "FeaturedContentHighlight",
  "Group",
  "GroupCsv",
  "Invoice",
  "Invoices",
  "MailToSupport",
  "PaymentHistory",
  "RecommendedInfluencers",
  "UGCMediaOperation",
  "VideoCodeOperation",
  "all",
] as const;
type AppAbilities = [
  (typeof actions)[number],
  (typeof subjects)[number] | ForcedSubject<Exclude<(typeof subjects)[number], "all">>,
];
type AppAbility = MongoAbility<AppAbilities>;
const createAppAbility = createMongoAbility as CreateAbility<AppAbility>;

type Roles =
  | "REFLUENCED_COMPANY_ADMIN" // "Traditional" Refluenced admin who can also act as a company
  | "REFLUENCED_INFLUENCER_ADMIN" // "Traditional" Refluenced admin
  | "ADMIN" // Brand owner
  | "WRITE"
  | "PURCHASE"
  | "READ";
type DefinePermissions = (builder: AbilityBuilder<AppAbility>) => void;

const rolePermissions: Record<Roles, DefinePermissions> = {
  REFLUENCED_COMPANY_ADMIN: ({can}) => {
    can("create", "all");
    can("read", "all");
    can("update", "all");
    can("delete", "all");
    can("list", "all");
    can("download", "all");
  },
  REFLUENCED_INFLUENCER_ADMIN: ({can, cannot}) => {
    can("create", "all");
    can("read", "all");
    can("update", "all");
    can("delete", "all");
    can("list", "all");
    can("download", "all");
    cannot("create", "CompanyMemberInvitation");
  },
  ADMIN: ({can, cannot}) => {
    can("create", "all");
    can("read", "all");
    can("update", "all");
    can("delete", "all");
    can("list", "all");
    can("download", "all");
    cannot("create", "ApplicantAdminOperation");
    cannot("create", "ContentReview");
    cannot("create", "DisciplinaryOperation");
    cannot("create", "VideoCodeOperation");
    cannot("download", "GroupCsv");
    cannot("read", "AdminMenu");
    cannot("read", "BrandPagesMenu");
    cannot("update", "AgencyDetails");
    cannot("update", "CampaignAdminOnlyOptions");
    cannot("update", "CommercialPhaseDeadline");
    cannot("update", "CompanyCredits");
  },
  WRITE: ({can, cannot}) => {
    can("create", "all");
    can("read", "all");
    can("update", "all");
    can("delete", "all");
    can("list", "all");
    can("download", "all");
    cannot("create", "ApplicantAdminOperation");
    cannot("create", "CompanyPaymentMethod");
    cannot("create", "ContentReview");
    cannot("create", "DisciplinaryOperation");
    cannot("create", "VideoCodeOperation");
    cannot("download", "GroupCsv");
    cannot("read", "AdminMenu");
    cannot("read", "BrandPagesMenu");
    cannot("delete", "CompanyMember");
    cannot("delete", "CompanyPaymentMethod");
    cannot("update", "AgencyDetails");
    cannot("update", "CampaignAdminOnlyOptions");
    cannot("update", "CompanyCredits");
    cannot("update", "CompanyPaymentMethod");
    cannot("update", "CompanyOwner");
    cannot("update", "CompanyPlan");
    cannot("update", "CommercialPhaseDeadline");
  },
  PURCHASE: ({can}) => {
    can("create", "BuyoutPurchase");
    can("create", "FavoriteContentLike");
    can("read", "CampaignPerformance");
    can("read", "ContentDetails");
    can("read", "PaymentHistory");
    can("update", "CompanyContentBuyouts");
    can("list", "CompanyPaymentMethod");
    can("download", "Content");
    can("download", "CampaignAddresses");
    can("download", "CampaignReport");
  },
  READ: ({}) => {},
};

const defineAbilityFor = (role: Roles): AppAbility => {
  const builder = new AbilityBuilder(createAppAbility);

  if (typeof rolePermissions[role] === "function") {
    rolePermissions[role](builder);
  } else {
    throw new Error(`Undefined role: ${role}`);
  }

  return builder.build();
};

const AbilityContext = createContext<AppAbility>(createAppAbility());
const Can = createContextualCan(AbilityContext.Consumer);

const PermissionProvider: FC<PropsWithChildren<{companyId?: number | null}>> = ({children, companyId}) => {
  const isAgency = useIsAgency();
  const selectedCompanyId = useAtomValue(selectedCompanyIdAtom);
  const user = useUserStore(state => state.user);
  const selectedBrand =
    user.sub_brands?.find(brand => brand.company.id === (companyId ?? selectedCompanyId)) || first(user.sub_brands);
  const role = getUserPermission({
    admin_access: isAgency || selectedBrand?.admin_access,
    purchase_access: selectedBrand?.purchase_access,
    write_access: selectedBrand?.write_access,
    user_type: user.user_type!,
    is_company: user.is_company,
  });
  const ability = defineAbilityFor(role);

  return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>;
};

export {AbilityContext, Can, defineAbilityFor, PermissionProvider};
